import { Stack } from 'component-library';
import { FC, HTMLAttributes } from 'react';
import { useTranslation } from 'react-i18next';
import { NameType, ValueType } from 'recharts/types/component/DefaultTooltipContent';

import { CustomBenchmarkSuccessResponse } from '@/api/rest/resources/types/benchmark';
import { PlotReportFactLabel, PlotReportFactLabelNameEnum, ValueClass } from '@/api/rest/resources/types/fact';
import { INDICATOR } from '@/api/rest/resources/types/IndicatorProduct';
import { getDisplayNumber } from '@/hooks/useDisplayNumber';
import { usePlotGroupFilterEndYear } from '@/hooks/usePlotGroupFilterEndYear';
import { usePlotGroupFilterStartYear } from '@/hooks/usePlotGroupFilterStartYear';
import { Logger } from '@/lib/logs/logger';
import { isNullish } from '@/utils/booleans';
import { printYear } from '@/utils/formatting/date';
import { getColorForIndicator } from '@/utils/getColorForIndicator';

import { ValueClassPill } from '../Pill/ValueClassPill';
import { Bar, Chart, ComposedChart, getXAxisProps, TooltipProps } from './components/Chart';
import { CustomActiveBar } from './components/CustomActiveBar';
import { ChartLegendBar } from './components/LegendBar';
import {
  ChartTooltip,
  // ChartTooltipConfidenceInterval,TODO: MVP-5260 - Bring Confidence Interval back to charts
  ChartTooltipTextPrimary,
  ChartTooltipTextSecondary,
} from './components/Tooltip';
import { ChartLegendBarItem } from './components/types';
import { NoChartData } from './NoChartData';
import { RenderReferenceLine } from './ReferenceLines';
import { ChartData, ChartProps } from './types';
import { getChartDataFromGraphFact, getXAxisDomainForHistoricGraph, sortChartData } from './utils';
import { getChartZoomedDomain } from './utils/getChartZoomedDomain';

const canopyHeightColors = getColorForIndicator(INDICATOR.CANOPY_HEIGHT);

const CANOPY_HEIGHT_LABEL = 'global.analysis.canopyHeight';
const RECTANGLE_RADIUS = 4;

export const chartColors = {
  canopyHeight: canopyHeightColors,
  // confidenceInterval: {
  //   fill: canopyHeightColors.confidenceInterval,
  // },
} as const;

const factNames = {
  canopy_height: 'canopyHeight',
};

export const CanopyHeightHistoricalChart: FC<
  HTMLAttributes<HTMLDivElement> &
    ChartProps & { plotGroupId?: string; customBenchmark?: CustomBenchmarkSuccessResponse | null }
> = ({ data, showTooltip, zoom, plotGroupId, customBenchmark, ...delegated }) => {
  const { t } = useTranslation();
  const unit = t('global.ui.unit.m');
  const filtersStartYear = usePlotGroupFilterStartYear(plotGroupId);
  const limitMaxYear = usePlotGroupFilterEndYear(plotGroupId);

  let dateDataMap = getChartDataFromGraphFact(data, factNames);
  if (customBenchmark) {
    const emptyfactValue = {
      value: null,
      upper_confidence_limit: null,
      lower_confidence_limit: null,
      confidence_interval_pct: null,
    };
    dateDataMap = Object.keys(dateDataMap).reduce((acc, key: string) => {
      acc[`${key}`] = {
        ...dateDataMap[`${key}`],
        custom_low: customBenchmark.low_value !== null ? { ...emptyfactValue, value: customBenchmark.low_value } : null,
        custom_moderate:
          customBenchmark.moderate_value !== null ? { ...emptyfactValue, value: customBenchmark.moderate_value } : null,
        custom_high:
          customBenchmark.high_value !== null ? { ...emptyfactValue, value: customBenchmark.high_value } : null,
      };
      return acc;
    }, {} as ChartData);
  }
  const sortedChartData = sortChartData(dateDataMap, (date) => new Date(date).getTime());

  const firstTick = sortedChartData.at(0);
  const lastTick = sortedChartData.at(-1);

  if (!firstTick || !lastTick) {
    Logger.error('Not enough data to render chart');
    return (
      <div className='relative flex h-full w-full flex-col justify-center' {...delegated}>
        <NoChartData />
      </div>
    );
  }

  const xTicksDomain = getXAxisDomainForHistoricGraph(firstTick.label, lastTick.label, filtersStartYear, limitMaxYear);

  const xAxisProps = {
    ...getXAxisProps(xTicksDomain),
    tickFormatter: printYear,
  };

  const yAxisDomain = getChartZoomedDomain({
    data,
    zoom,
  });

  return (
    <>
      <Chart.Container
        {...delegated}
        data={sortedChartData}
        config={{
          canopyHeight: {
            label: t(CANOPY_HEIGHT_LABEL),
            color: chartColors.canopyHeight.stroke,
          },
        }}
      >
        <ComposedChart data={sortedChartData}>
          <defs>
            <linearGradient id='canopyHeight' x1='0' y1='-2.6' x2='0' y2='1'>
              <stop offset='0%' stopColor={chartColors.canopyHeight.fill} stopOpacity={0} />
              <stop offset='100%' stopColor={chartColors.canopyHeight.fill} stopOpacity={1} />
            </linearGradient>
          </defs>
          <Bar
            fill='url(#canopyHeight)'
            dataKey='canopyHeight.value'
            radius={[RECTANGLE_RADIUS, RECTANGLE_RADIUS, 0, 0]}
            activeBar={<CustomActiveBar ry={RECTANGLE_RADIUS} fill={chartColors.canopyHeight.fill} />}
            className='canopy-height-bar'
            barSize={21}
          />
          {customBenchmark && (
            <>
              {customBenchmark.low_value !== null &&
                RenderReferenceLine({
                  dataKey: 'custom_low.value',
                  type: 'monotone',
                  label: `${customBenchmark.name} (${t('global.benchmark.low')})`,
                  chartData: sortedChartData,
                  id: 'custom-low',
                  labelSide: 'right',
                })}
              {customBenchmark.moderate_value !== null &&
                RenderReferenceLine({
                  dataKey: 'custom_moderate.value',
                  type: 'monotone',
                  label: `${customBenchmark.name} (${t('global.benchmark.moderate')})`,
                  chartData: sortedChartData,
                  id: 'custom-moderate',
                  labelSide: 'right',
                })}
              {customBenchmark.high_value !== null &&
                RenderReferenceLine({
                  dataKey: 'custom_high.value',
                  type: 'monotone',
                  label: `${customBenchmark.name} (${t('global.benchmark.high')})`,
                  chartData: sortedChartData,
                  id: 'custom-high',
                  labelSide: 'right',
                })}
            </>
          )}
          <Chart.XAxis {...xAxisProps} className='canopy-height-x-axis' padding={{ left: 40 }} />
          <Chart.YAxis domain={yAxisDomain} className='canopy-height-y-axis' allowDataOverflow={true}>
            <Chart.Label>{unit}</Chart.Label>
          </Chart.YAxis>
          {showTooltip && <Chart.Tooltip content={<CustomTooltip />} cursor={false} />}
        </ComposedChart>
      </Chart.Container>
      <CustomLegendBar />
    </>
  );
};

const CustomLegendBar = () => {
  const { t } = useTranslation();

  const legendBarItems: ChartLegendBarItem[] = [
    {
      label: t(CANOPY_HEIGHT_LABEL),
      color: chartColors.canopyHeight.fill,
    },
    // {
    //   label: t('shared.ncaDetail.details.explainers.confidenceInterval.title'),
    //   color: chartColors.confidenceInterval.fill ?? '',
    //   shape: (
    //     <span
    //       className='h-[2px] w-[14px]'
    //       style={{
    //         backgroundColor: chartColors.confidenceInterval.fill,
    //       }}
    //     />
    //   ),
    //   popoverContent: {
    //     title: t('shared.ncaDetail.details.explainers.confidenceInterval.title'),
    //     body: t('shared.ncaDetail.details.explainers.confidenceInterval.body'),
    //   },
    // },
  ];

  return (
    <ChartLegendBar items={legendBarItems} className='mt-8 sm:ml-[28px]' data-testid='canopy-height-chart-legend' />
  );
};

const CustomTooltip = ({ active, payload, label }: TooltipProps<ValueType, NameType>) => {
  const { t } = useTranslation();
  const unit = t('global.ui.unit.m');

  const canopyHeightValue = payload?.find((data) => data.dataKey === 'canopyHeight.value');
  const canopyHeightDisplay = !isNullish(canopyHeightValue?.value)
    ? `${getDisplayNumber(canopyHeightValue?.value as string, window.navigator.language)} ${unit}`
    : t('global.analysis.noData');

  const dataPointHasLabel = canopyHeightValue && canopyHeightValue?.payload?.canopyHeight?.labels?.length > 0;
  const dataPointValueClass = dataPointHasLabel
    ? canopyHeightValue.payload.canopyHeight.labels.find(
        (dataLabel: PlotReportFactLabel) => dataLabel.name === PlotReportFactLabelNameEnum.value_class,
      )
    : undefined;
  const dataLabel = dataPointValueClass?.value as ValueClass;

  if (!active) return null;

  return (
    <ChartTooltip>
      <Stack spacing={8} direction='row' className='justify-between'>
        <ChartTooltipTextSecondary>{printYear(label)}</ChartTooltipTextSecondary>
        {dataLabel && <ValueClassPill title={dataLabel} />}
      </Stack>
      <Stack spacing={8} direction='row'>
        <ChartTooltipTextPrimary>
          {t('shared.ncaDetail.details.forest.labels.averageCanopyHeight')}
        </ChartTooltipTextPrimary>
        <ChartTooltipTextPrimary>{canopyHeightDisplay}</ChartTooltipTextPrimary>
      </Stack>
      {/* TODO: MVP-5260 - Bring Confidence Interval back to charts  */}
      {/* <ChartTooltipConfidenceInterval ciPct={canopyHeightValue?.payload?.canopyHeight?.confidence_interval_pct} /> */}
    </ChartTooltip>
  );
};

export { CustomTooltip as CustomTooltipCanopyHeightHistorical };
